import { useCallback, useMemo, useState } from 'react';
import { SlideKind } from '@duke-hq/svc-api-db';

import type { AssessorSlide } from '../types';
import { needsAssessorAttention } from '../utils';

export function useAssessorSlides({
  assessorSlides,
  isAlreadyAssessed,
}: {
  assessorSlides: AssessorSlide[];
  isAlreadyAssessed: boolean;
}) {
  const firstAssessableIndex = assessorSlides.findIndex(
    ({ isManuallyAssessable }) => isManuallyAssessable,
  );
  const firstAssessableSlideId = assessorSlides[firstAssessableIndex]?.slideId ?? null;
  const firstUnresolvedAssessableIndex = assessorSlides.findIndex(needsAssessorAttention);
  const hasAssessableSlides = firstAssessableIndex !== -1;
  const hasUnresolvedAssessable = firstUnresolvedAssessableIndex !== -1;
  const startOnOverview = isAlreadyAssessed || (hasAssessableSlides && !hasUnresolvedAssessable);

  let lastMarkedAssessableIndex = -1;
  for (let i = assessorSlides.length - 1; i >= 0; i -= 1) {
    const slide = assessorSlides[i];
    if (slide?.isManuallyAssessable === true && slide.correct !== null) {
      lastMarkedAssessableIndex = i;
      break;
    }
  }

  const [selectedSlideId, setSelectedSlideId] = useState<string | undefined>(() => {
    if (startOnOverview) {
      return assessorSlides[assessorSlides.length - 1]?.slideId;
    }
    if (hasUnresolvedAssessable) {
      return assessorSlides[firstUnresolvedAssessableIndex]?.slideId;
    }
    return firstAssessableSlideId ?? undefined;
  });

  const [isOverviewScreen, setIsOverviewScreen] = useState(startOnOverview);
  const [answerInput, setAnswerInput] = useState<string>('');
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | undefined>();
  const [lastSyncedSlideId, setLastSyncedSlideId] = useState<string | undefined>();

  const index = assessorSlides.findIndex((slide) => slide.slideId === selectedSlideId);
  const currentSlideIndex = index > -1 ? index : 0;
  const currentSlide = assessorSlides[currentSlideIndex];

  // Sync the editable answer state when the slide changes.
  if (currentSlide?.slideId !== lastSyncedSlideId) {
    setLastSyncedSlideId(currentSlide?.slideId);
    setAnswerInput(currentSlide?.answer?.userInput ?? '');

    if (currentSlide?.kind === SlideKind.QUESTION_GROUP && currentSlide.answer?.answerId) {
      setSelectedAnswerId(currentSlide.answer.answerId[0]);
    } else {
      setSelectedAnswerId(undefined);
    }
  }

  const initialIndex = startOnOverview
    ? Math.max(0, assessorSlides.length - 1)
    : Math.max(0, firstAssessableIndex, firstUnresolvedAssessableIndex, lastMarkedAssessableIndex);
  const [maxVisitedIndex, setMaxVisitedIndex] = useState(initialIndex);

  if (currentSlideIndex > maxVisitedIndex) {
    setMaxVisitedIndex(currentSlideIndex);
  }

  const selectSlide = useCallback((slideId: string) => {
    setSelectedSlideId(slideId);
    setIsOverviewScreen(false);
  }, []);

  const showOverview = useCallback(() => {
    if (!isAlreadyAssessed) {
      const firstUnresolved = assessorSlides.find(needsAssessorAttention);

      if (firstUnresolved != null) {
        selectSlide(firstUnresolved.slideId);
        return;
      }
    }

    const lastSlide = assessorSlides[assessorSlides.length - 1];

    if (lastSlide?.slideId != null) {
      setSelectedSlideId(lastSlide.slideId);
    }

    setIsOverviewScreen(true);
  }, [assessorSlides, isAlreadyAssessed, selectSlide]);

  const onNext = useCallback(() => {
    const nextSlide = assessorSlides[currentSlideIndex + 1];

    if (nextSlide?.slideId == null) {
      showOverview();
      return;
    }

    selectSlide(nextSlide.slideId);
  }, [assessorSlides, currentSlideIndex, selectSlide, showOverview]);

  const onNextUnmarked = useCallback(() => {
    const nextUnresolved = assessorSlides.slice(currentSlideIndex + 1).find(needsAssessorAttention);

    if (nextUnresolved?.slideId == null) {
      showOverview();
      return;
    }

    selectSlide(nextUnresolved.slideId);
  }, [assessorSlides, currentSlideIndex, selectSlide, showOverview]);

  const onPrevious = useCallback(() => {
    if (isOverviewScreen) {
      setIsOverviewScreen(false);
      return;
    }

    const previousSlide = assessorSlides[currentSlideIndex - 1];
    if (previousSlide?.slideId != null) {
      setSelectedSlideId(previousSlide.slideId);
    }
  }, [isOverviewScreen, assessorSlides, currentSlideIndex]);

  const canShowOverview = isAlreadyAssessed || !assessorSlides.some(needsAssessorAttention);

  const progress = useMemo(() => {
    const total = assessorSlides.length;

    if (total === 0) {
      return 0;
    }

    return Math.min(Math.ceil((currentSlideIndex / total) * 100), 100);
  }, [currentSlideIndex, assessorSlides]);

  return useMemo(
    () => ({
      currentSlide,
      currentSlideIndex,
      answerInput,
      selectedAnswerId,
      setSelectedAnswerId,
      onNext,
      onNextUnmarked,
      onPrevious,
      selectSlide,
      showOverview,
      canShowOverview,
      isOverviewScreen,
      maxVisitedIndex,
      progress,
    }),
    [
      currentSlide,
      currentSlideIndex,
      answerInput,
      selectedAnswerId,
      setSelectedAnswerId,
      onNext,
      onNextUnmarked,
      onPrevious,
      selectSlide,
      showOverview,
      canShowOverview,
      isOverviewScreen,
      maxVisitedIndex,
      progress,
    ],
  );
}
